'use client';

/**
 * 카테고리 메인/서브 컨테이너.
 * isCateMain → 구좌 리스트, hasLp → LP(cateList 면 추천 Row 포함)
 */
import type {
  CategoryMainPageKey,
  CategoryNavigationItem,
} from '@apis/renewal/categoryMain';
import DesktopHeaderLayoutContainer from '@components/pages/common/DesktopHeaderLayoutContainer';
import HeaderLayout from '@components/pages/common/HeaderLayoutContainer';
import type { CornerAreaSourceParams } from '@components/cornerArea/cornerAreaSource';
import DesktopSkeleton from '@components/pages/common/DesktopSkeleton';
import SubSkeleton03 from '@wconcept/wconcept-ui-renewal/components/molecules/_skeleton/SubSkeleton03';
import { createCategoryMainAreaSource } from '@components/cornerArea/sources/categoryMain';
import CategoryPathTabs from '@components/pages/category/CategoryPathTabs';
import CategoryProductList from '@components/pages/category/CategoryProductList';
import type { LpCategoryPath } from '@apis/renewal/lpProduct';
import { useLoading } from '@contexts/LoadingContext';
import { getCategoryTypeName, getShopDisplayName } from '@entities/category';
import {
  getNavigationItemValue,
  useCategoryNavigationTabs,
} from '@hooks/category/useCategoryNavigationTabs';
import { useCategoryBreadcrumb } from '@hooks/category/useCategoryBreadcrumb';
import { useScrollRestoration } from '@hooks/restoreScrollPosition';
import useGlobalState from '@hooks/useGlobalState';
import useAirBridgeEvent from '@hooks/useAirBridgeEvent';
import useHistoryRouter from '@hooks/useLinkTo';
import type { CategoryPageServerProps } from '@models/categoryMain';
import Button from '@wconcept/wconcept-ui-renewal/components/atoms/Button';
import Text from '@wconcept/wconcept-ui-renewal/src/components/atoms/Text';
import Title from '@wconcept/wconcept-ui-renewal/src/components/atoms/Title';
import { IconArrowDown01 } from '@wconcept/wconcept-ui-renewal/src/components/atoms/svg';
import BreadcrumbDesktop from '@wconcept/wconcept-ui-renewal/components/molecules/_desktop/BreadcrumbDesktop';
import CategoryDimLayer from '@wconcept/wconcept-ui-renewal/src/components/organisms/_list/CategoryDimLayer';
import CategoryTemplate from '@wconcept/wconcept-ui-renewal/components/templates/CategoryTemplate';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';

const pageClassName = 'category-page';

const CornerAreaList = lazy(
  () => import('@components/cornerArea/CornerAreaList')
);

const AREA_LIST_LOADING_KEY = 'categoryAreaList';
const LP_LOADING_KEY = 'categoryProductList';

function CategoryPageContainer(): JSX.Element | null {
  const { isMobile, initialData } = useGlobalState<CategoryPageServerProps>();
  const { linkTo } = useHistoryRouter();
  const { openAirBridgeAppBanner } = useAirBridgeEvent();

  // 앱 설치 스마트배너 (MO 웹)
  useEffect(() => {
    openAirBridgeAppBanner();
  }, [openAirBridgeAppBanner]);

  const isCateMain =
    initialData?.pageType === 'cateMain' &&
    initialData?.hasAreaDisplay !== false;
  const hasLp = !!initialData?.lp;
  const loadingKeys = [
    ...(isCateMain ? [AREA_LIST_LOADING_KEY] : []),
    ...(hasLp ? [LP_LOADING_KEY] : []),
  ];
  const { isAllLoaded } = useLoading(loadingKeys);
  useScrollRestoration(loadingKeys.length ? isAllLoaded : true);

  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);

  useEffect(() => {
    if (!isSubmenuOpen) {
      return undefined;
    }
    document.body.classList.add('no-scroll');
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isSubmenuOpen]);

  const mediumCd = initialData?.mediumCd ?? '';
  const largeCategory = initialData?.largeCategory || '000';
  const middleCategory = initialData?.middleCategory || '000';
  const navigation = initialData?.navigation;

  const pageKey: CategoryMainPageKey = useMemo(
    () => ({
      mediumCd,
      categoryCd: largeCategory !== '000' ? largeCategory : null,
    }),
    [mediumCd, largeCategory]
  );
  const recommendItems = initialData?.recommendItems;
  const createAreaSource = useCallback(
    (ctx: CornerAreaSourceParams) =>
      createCategoryMainAreaSource({
        pageKey,
        recommendItems,
        isMobile: !!isMobile,
        ...ctx,
      }),
    [pageKey, recommendItems, isMobile]
  );

  const smallCategory = initialData?.smallCategory || '000';

  const lpPath: LpCategoryPath = useMemo(
    () => ({
      mediumCd,
      largeCategory,
      ...(initialData?.middleCategory
        ? { middleCategory: initialData.middleCategory }
        : {}),
      ...(initialData?.smallCategory
        ? { smallCategory: initialData.smallCategory }
        : {}),
    }),
    [
      mediumCd,
      largeCategory,
      initialData?.middleCategory,
      initialData?.smallCategory,
    ]
  );

  // 3·4뎁스 탭 / 딤레이어 데이터
  const {
    shortcutList,
    depth3List,
    depth4List,
    selectedDepth3,
    selectedDepth4,
    dimLayerCategoryList,
    dimLayerActiveValue,
    dimLayerActiveValueDepth2,
    findNavigationItemByValue,
  } = useCategoryNavigationTabs({
    navigation,
    mediumCd,
    largeCategory,
    middleCategory,
    smallCategory,
  });

  const breadcrumbItems = useCategoryBreadcrumb({
    navigationPc: initialData?.navigationPc,
    mediumCd,
    largeCategory,
    middleCategory,
    smallCategory,
  });

  const categoryType = initialData?.categoryType;
  if (!categoryType) {
    return null;
  }

  const shopCds = initialData?.shopCds ?? [];
  const shopDisplayName = getShopDisplayName(shopCds);
  const categoryPath = navigation?.categoryPath ?? [];
  const currentTitle =
    shopDisplayName ||
    (isMobile ? categoryPath[1] : categoryPath[categoryPath.length - 1]) ||
    categoryPath[0] ||
    getCategoryTypeName(categoryType);

  const content = (
    <CategoryTemplate className="category-template">
      <div className="contents-wrap">
        <CategoryPathTabs
          depth3List={depth3List}
          depth4List={depth4List}
          selectedDepth3Value={
            selectedDepth3 && getNavigationItemValue(selectedDepth3)
          }
          selectedDepth4Value={
            selectedDepth4 && getNavigationItemValue(selectedDepth4)
          }
        />
        {isCateMain && (
          <Suspense
            fallback={isMobile ? <SubSkeleton03 /> : <DesktopSkeleton />}
          >
            <CornerAreaList
              id={AREA_LIST_LOADING_KEY}
              createSource={createAreaSource}
            />
          </Suspense>
        )}
      </div>
      {hasLp && (
        <CategoryProductList
          id={LP_LOADING_KEY}
          lpPath={lpPath}
          shopCds={shopCds}
          // 추천 Row 는 cateList 에서만
          showRecommend={!isCateMain}
        />
      )}
    </CategoryTemplate>
  );

  if (!isMobile) {
    return (
      <DesktopHeaderLayoutContainer
        className={pageClassName}
        pageTitle={currentTitle}
        breadcrumbNode={
          breadcrumbItems && <BreadcrumbDesktop items={breadcrumbItems} />
        }
        megaMenu={initialData?.megaMenu}
        gnbInfo={initialData?.gnbInfo || []}
        searchInitInfo={initialData?.searchInitInfo}
      >
        {content}
      </DesktopHeaderLayoutContainer>
    );
  }

  const navigateToNavigationItem = (
    target: CategoryNavigationItem | undefined
  ): void => {
    if (!target) {
      return;
    }
    setIsSubmenuOpen(false);
    if (target.landingUrl) {
      linkTo.push({ url: target.landingUrl });
    }
  };

  const titleNode = shortcutList.length ? (
    <Title className={`cate-title${isSubmenuOpen ? ' active' : ''}`}>
      <Button
        onClick={() => {
          setIsSubmenuOpen(open => !open);
        }}
      >
        <Text ellipsis={1}>{currentTitle}</Text>
        <IconArrowDown01 />
      </Button>
    </Title>
  ) : undefined;

  return (
    <HeaderLayout
      className={pageClassName}
      gnbType="N"
      subHeaderType="subDetail"
      gnbInfo={initialData?.gnbInfo}
      logoInfo={initialData?.logoInfo?.biLogo}
      pageTitle={currentTitle}
      titleNode={titleNode}
      isProfileBtn={false}
      isFooter={false}
    >
      <CategoryDimLayer
        isOpen={isSubmenuOpen}
        onClose={() => {
          setIsSubmenuOpen(false);
        }}
        categoryList={dimLayerCategoryList}
        activeCategoryValue={dimLayerActiveValue}
        activeCategoryValueDepth2={dimLayerActiveValueDepth2}
        onClickDepth1={(depth1Value, hasChildren) => {
          // 하위 목록 없는 항목은 바로 이동
          if (!hasChildren) {
            navigateToNavigationItem(findNavigationItemByValue(depth1Value));
          }
        }}
        onClickDepth2={(_depth1Value, depth2Value) => {
          navigateToNavigationItem(findNavigationItemByValue(depth2Value));
        }}
      />
      {content}
    </HeaderLayout>
  );
}

export default CategoryPageContainer;
